import { Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { PREFECTURES_, getPrefectureNameJpByPref } from '~/lib/prefectureHelper'
import { TEL_WITHOUT_HYPHEN_REGEXP, POSTAL_CODE_WITHOUT_HYPHEN_REGEXP } from '~/lib/constants'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { CAMPAIGN_FORM_TITLE, CAMPAIGN_ERROR_MESSAGE } from '~/constants/campaign'
import { CampaignCategory, Pref, ReviewSection, ReviewTarget, FormatVersion } from '~/generated/graphql'
import { z } from 'zod'
import { isValidPhoneNumber } from '~/lib/phoneNumber'
import InfoIcon from '@mui/icons-material/Info'
import { UnsetAlert } from '~/components/common/UnsetAlert'
import { GOOGLE_MAPS_EMBED_API_KEY, APP_ENV } from '~/lib/config'
import { CreateCampaignState } from './state'
import { PostalCodeSearch } from './PostalCodeSearch'
import { StationSearch } from './StationSearch'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'

function valueRequired(value: string | null | undefined, ctx: z.RefinementCtx) {
  if (!value?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: CAMPAIGN_ERROR_MESSAGE.REQUIRED,
    })
    return z.NEVER
  }
  return value as string
}

const storeInfoSchemaFields = {
  cityName: z
    .string()
    .min(1, { message: CAMPAIGN_ERROR_MESSAGE.REQUIRED })
    .max(255, { message: CAMPAIGN_ERROR_MESSAGE.MAX_LENGTH })
    .nullish()
    .transform(valueRequired),
  streetAddress: z
    .string()
    .min(1, { message: CAMPAIGN_ERROR_MESSAGE.REQUIRED })
    .max(255, { message: CAMPAIGN_ERROR_MESSAGE.MAX_LENGTH })
    .nullish()
    .transform(valueRequired),
  buildingName: z.string().max(255, { message: CAMPAIGN_ERROR_MESSAGE.MAX_LENGTH }).nullish().nullable(),
  shopName: z
    .string()
    .min(1, {
      message: CAMPAIGN_ERROR_MESSAGE.REQUIRED,
    })
    .max(255, { message: CAMPAIGN_ERROR_MESSAGE.MAX_LENGTH })
    .nullish()
    .transform(valueRequired),
  nearestStationName: z
    .string()
    .min(1, {
      message: CAMPAIGN_ERROR_MESSAGE.REQUIRED,
    })
    .max(255, { message: CAMPAIGN_ERROR_MESSAGE.MAX_LENGTH })
    .nullish()
    .transform(valueRequired),
  shopPostalCode: z
    .string()
    .regex(POSTAL_CODE_WITHOUT_HYPHEN_REGEXP, {
      message: CAMPAIGN_ERROR_MESSAGE.POSTAL_CODE_LENGTH,
    })
    .nullish()
    .transform(valueRequired),
  shopPref: z
    .nativeEnum(Pref)
    .or(z.literal(''))
    .nullish()
    .transform((v, ctx) => {
      if (v === undefined || v === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: CAMPAIGN_ERROR_MESSAGE.REQUIRED,
        })
        return z.NEVER
      }
      return v as Pref
    }),
  shopTel: z
    .string()
    .regex(TEL_WITHOUT_HYPHEN_REGEXP, {
      message: CAMPAIGN_ERROR_MESSAGE.TEL_LENGTH,
    })
    .refine((value) => isValidPhoneNumber(value), {
      message: CAMPAIGN_ERROR_MESSAGE.TEL_FORMAT,
    })
    .nullish()
    .transform(valueRequired),
  shopTemplateId: z.number().nullable(),
}

const storeNullFields = {
  // 設定しないとエラーになるのでダミー
  cityName: z
    .string()
    .nullish()
    .transform(() => null),
  streetAddress: z
    .string()
    .nullish()
    .transform(() => null),
  buildingName: z
    .string()
    .nullish()
    .transform(() => null),
  shopName: z
    .string()
    .nullish()
    .transform(() => null),
  nearestStationName: z
    .string()
    .nullish()
    .transform(() => null),
  shopPostalCode: z
    .string()
    .nullish()
    .transform(() => null),
  shopPref: z
    .nativeEnum(Pref)
    .or(z.literal(''))
    .nullish()
    .transform(() => null),
  shopTel: z
    .string()
    .nullish()
    .transform(() => null),
  shopTemplateId: z
    .number()
    .nullable()
    .transform(() => null),
}

export const storeInfoSchema = z.discriminatedUnion('category', [
  z.object({
    category: z.literal(CampaignCategory.Service),
    ...storeInfoSchemaFields,
  }),
  z.object({
    category: z.literal(CampaignCategory.Product),
    ...storeNullFields,
  }),
  z.object({
    category: z.literal(null),
    ...storeNullFields,
  }),
])

type Props = CampaignInputComponentProps

export const StoreInfo = ({ description }: Props) => {
  const { control, watch } = useFormContext<CreateCampaignState>()

  // 住所取得エラー
  const [addressFetchError, setAddressFetchError] = useState(false)
  const [postalCodeNoResults, setPostalCodeNoResults] = useState(false)
  const [stationSearchError, setStationSearchError] = useState(false)

  // 手動入力を有効にする条件（APIエラーまたは結果0件）
  const shouldEnableManualInput = addressFetchError || postalCodeNoResults

  const category = watch('category')
  const shopPref = watch('shopPref')
  const cityName = watch('cityName')
  const streetAddress = watch('streetAddress')
  const buildingName = watch('buildingName')

  if (category !== CampaignCategory.Service) {
    return null
  }

  return (
    <CampaignInputComponent
      title={CAMPAIGN_FORM_TITLE.shopAddress}
      required={true}
      description={description}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.ShopAddress],
      }}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      <Stack gap={3}>
        <FormControl fullWidth sx={{ pt: 2 }}>
          <Controller
            name="shopName"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="店舗名"
                placeholder="トリドリホテル"
                error={!!error?.message}
                helperText={<ValidationErrorMessage error={error?.message} />}
                inputRef={field.ref}
                slotProps={{
                  inputLabel: { shrink: !!field.value },
                }}
              />
            )}
          />
        </FormControl>

        <PostalCodeSearch
          addressFetchError={addressFetchError}
          setAddressFetchError={setAddressFetchError}
          postalCodeNoResults={postalCodeNoResults}
          setPostalCodeNoResults={setPostalCodeNoResults}
        />

        <Controller
          name="shopPref"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error?.message}>
              <InputLabel
                id="shop-pref-label"
                shrink={!!field.value}
                sx={(theme) => ({
                  color: theme.palette.text.disabled,
                  // 値が空の場合は中央に配置
                  ...(!field.value
                    ? {
                        top: '8.5px',
                        paddingLeft: '14px',
                        transform: 'none',
                      }
                    : {}),
                  '&.Mui-error': {
                    color: `${theme.palette.error.main} !important`,
                  },
                })}
              >
                都道府県
              </InputLabel>
              <Select
                {...field}
                id="shop-pref"
                labelId="shop-pref-label"
                label="都道府県"
                disabled={!shouldEnableManualInput}
                sx={(theme) => ({
                  backgroundColor: shouldEnableManualInput ? 'transparent' : theme.palette.action.hover,
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme.palette.text.disabled,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.other.disabled,
                  },
                  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.error.main,
                  },
                })}
              >
                <MenuItem disabled value="">
                  都道府県を選択してください
                </MenuItem>
                {PREFECTURES_.map((pref) => (
                  <MenuItem key={pref.en} value={pref.en}>
                    {pref.jp}
                  </MenuItem>
                ))}
              </Select>
              <ValidationErrorMessage error={error?.message} sx={{ pl: 2 }} />
            </FormControl>
          )}
        />

        <FormControl fullWidth>
          <Controller
            name="cityName"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="市区町村名"
                placeholder="渋谷区"
                error={!!error?.message}
                helperText={<ValidationErrorMessage error={error?.message} />}
                inputRef={field.ref}
                disabled={!shouldEnableManualInput}
                slotProps={{
                  inputLabel: { shrink: !!field.value },
                }}
                sx={(theme) => ({
                  '& .MuiInputBase-root.Mui-disabled': {
                    backgroundColor: theme.palette.action.hover,
                  },
                  '& .MuiInputLabel-root.Mui-disabled': {
                    color: theme.palette.text.disabled,
                  },
                  '& .MuiInputLabel-root.Mui-error': {
                    color: theme.palette.error.main,
                  },
                  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.error.main,
                  },
                })}
              />
            )}
          />
        </FormControl>

        <FormControl fullWidth>
          <Controller
            name="streetAddress"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="番地"
                placeholder="1-1-1"
                error={!!error?.message}
                helperText={<ValidationErrorMessage error={error?.message} />}
                inputRef={field.ref}
                slotProps={{
                  inputLabel: { shrink: !!field.value },
                }}
              />
            )}
          />
        </FormControl>

        <FormControl fullWidth>
          <Controller
            name="buildingName"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="建物名等（任意）"
                placeholder="トリドリビル3F"
                error={!!error?.message}
                helperText={<ValidationErrorMessage error={error?.message} />}
                inputRef={field.ref}
                slotProps={{
                  inputLabel: { shrink: !!field.value },
                }}
              />
            )}
          />
        </FormControl>

        <StationSearch stationSearchError={stationSearchError} setStationSearchError={setStationSearchError} />

        <FormControl fullWidth>
          <Controller
            name="shopTel"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="店舗電話番号"
                placeholder="0312345678"
                error={!!error?.message}
                helperText={<ValidationErrorMessage error={error?.message} />}
                inputMode="numeric"
                inputRef={field.ref}
                slotProps={{
                  inputLabel: { shrink: !!field.value },
                }}
              />
            )}
          />
        </FormControl>

        {shopPref && cityName && streetAddress ? (
          <Stack gap={1}>
            <Box sx={{ height: '100%', pt: 0.5 }}>
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                allowFullScreen
                style={{ minHeight: '250px', border: 'none' }}
                src={`https://www.google.com/maps/embed/v1/place?key=${APP_ENV === 'local' ? process.env.NEXT_PUBLIC_GOOGLE_MAPS_EMBED_API_KEY : GOOGLE_MAPS_EMBED_API_KEY}&q=${
                  getPrefectureNameJpByPref(shopPref) ?? ''
                }${cityName}${streetAddress}+${buildingName ?? ''}`}
              />
            </Box>
            <Typography variant="caption">
              ※マップ上のピンが正しい住所で表示されているかをご確認ください。ピンの位置が正しくない場合は、入力された住所に誤りがある可能性があります。
            </Typography>
          </Stack>
        ) : (
          <UnsetAlert icon={<InfoIcon color="action" />} sx={{ mt: 1 }}>
            都道府県、市区町村名、番地を入力すると、Google Mapが表示されます
          </UnsetAlert>
        )}
      </Stack>
    </CampaignInputComponent>
  )
}
